import React, { useRef, useState } from 'react'
import { Card, Chip, Checkbox, Icon } from '@arnsbogroup/react-voicearchive'
import { useIntl } from 'react-intl'
import useAudioRecorder from 'hooks/useAudioRecorder'
import MemoryRecordCard from 'components/MemoryRecordCard'
import { MemoryStatusChip } from 'components/datatable'
import messages from './messages'

const ActiveRecorder = ({ memory, onDone, showUpload = true }) => {
  const [preview, setPreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const currentMemoryIdRef = useRef(null)

  const onStop = (blob) => {
    const memoryId = currentMemoryIdRef.current
    if (!memoryId || !blob) return
    const reader = new FileReader()
    reader.onload = () => setPreview(reader.result)
    reader.readAsDataURL(blob)
    currentMemoryIdRef.current = null
  }

  const { record, isRecording, analyserNode } = useAudioRecorder({ onStop })

  const startRecording = () => {
    currentMemoryIdRef.current = memory.uid
    record(true)
  }

  const stopRecording = () => record(false)

  const reRecord = () => {
    setPreview(null)
    currentMemoryIdRef.current = memory.uid
    record(true)
  }

  const submitSrc = (src) => {
    setSubmitting(true)
    fetch(`/rest/v2/memoryrequest/${memory.recording_request_hash}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ memoryId: memory.uid, src }),
    })
      .then((r) => r.json())
      .then(({ success, errors }) => {
        if (!success) { alert((errors || []).join(', ')); return }
        onDone()
      })
      .catch((e) => alert(e.message))
      .finally(() => setSubmitting(false))
  }

  return (
    <MemoryRecordCard
      memory={memory}
      isRecording={isRecording}
      preview={preview}
      submitting={submitting}
      analyserNode={analyserNode}
      onStartRecord={startRecording}
      onStopRecord={stopRecording}
      onReRecord={reRecord}
      onSubmitPreview={() => submitSrc(preview)}
      onUpload={(file) => {
        if (!file) return
        setSubmitting(true)
        const reader = new FileReader()
        reader.onload = () => submitSrc(reader.result)
        reader.readAsDataURL(file)
      }}
      showUpload={showUpload}
    />
  )
}

const MemoryListBlock = ({
  title = undefined,
  memories = [],
  compact = false,
  canDelete = false,
  canSelect = false,
  selectedIds = [],
  onDelete = () => {},
  onSelectionChange = () => {},
  onRecord = null,
  showUpload = false,
}) => {
  const { formatMessage } = useIntl()
  const [playingUid, setPlayingUid] = useState(null)
  const audioRefs = useRef({})

  if (!memories.length) {
    return null
  }

  const displayTitle = title === undefined ? formatMessage(messages.defaultTitle) : title

  const toggleSelect = (uid) => {
    if (selectedIds.includes(uid)) {
      onSelectionChange(selectedIds.filter((id) => id !== uid))
    } else {
      onSelectionChange([...selectedIds, uid])
    }
  }

  const togglePlay = (uid) => {
    const audio = audioRefs.current[uid]
    if (!audio) return

    if (playingUid === uid) {
      audio.pause()
      audio.currentTime = 0
      setPlayingUid(null)
    } else {
      if (playingUid !== null && audioRefs.current[playingUid]) {
        audioRefs.current[playingUid].pause()
        audioRefs.current[playingUid].currentTime = 0
      }
      audio.play().catch(() => {})
      setPlayingUid(uid)
    }
  }

  return (
    <Card className={compact ? 'p-3 space-y-2' : 'p-4 space-y-3'}>
      {displayTitle !== null && (
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">{displayTitle}</h3>
          <Chip size="xs">{memories.length}</Chip>
        </div>
      )}

      <div className="space-y-3">
        {memories.map((memory) => {
          const canRecord = memory.allow_record && memory.recording_request_hash && onRecord

          if (canRecord) {
            return (
              <ActiveRecorder
                key={memory.uid}
                memory={memory}
                onDone={onRecord}
                showUpload={showUpload}
              />
            )
          }

          return (
            <div key={memory.uid} className="border rounded p-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {canSelect && (
                  <Checkbox
                    checked={selectedIds.includes(memory.uid)}
                    onChange={() => toggleSelect(memory.uid)}
                    className="shrink-0"
                  />
                )}
                <div className="flex-1 text-sm">{memory.text || '-'}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {!!memory.language_name && <Chip size="xs">{memory.language_name}</Chip>}
                  <MemoryStatusChip memory={memory} />
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {memory.audio_download_url && (
                  <>
                    <audio
                      ref={(el) => { audioRefs.current[memory.uid] = el }}
                      src={memory.audio_stream_url}
                      onEnded={() => setPlayingUid(null)}
                    />
                    <button
                      onClick={() => togglePlay(memory.uid)}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-primary hover:text-white"
                      title={playingUid === memory.uid ? formatMessage(messages.stopTitle) : formatMessage(messages.playTitle)}
                    >
                      <Icon icon={['fas', playingUid === memory.uid ? 'stop' : 'play']} />
                    </button>
                    <a
                      href={memory.audio_download_url}
                      download
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-primary hover:text-white"
                      title={formatMessage(messages.downloadAudioTitle)}
                    >
                      <Icon icon={['fas', 'download']} />
                    </a>
                  </>
                )}

                {canDelete && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onDelete(memory)}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                      title={formatMessage(messages.deleteTitle)}
                    >
                      <Icon icon={['fas', 'trash']} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

export default MemoryListBlock
